import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Send, Mic, Paperclip, Square, ExternalLink, BookmarkPlus, Copy, Volume2, FolderPlus, Save, Bell, MoreHorizontal, Filter, Loader2 } from 'lucide-react';
import { api, API_BASE, getAuthToken } from '@/lib/api';
import { toast } from 'sonner';
import { ARI, SEEK, RESULTS } from '@/constants/testIds';
import { fmtCurrency, fmtDistance, timeAgo } from '@/lib/format';
import { sourceStatusLabel, sourceStatusClass } from '@/lib/scanStatus';
import { pushActiveScanToExtension, clearActiveScanFromExtension } from '@/lib/extensionBridge';
import { SaveToCollectionModal } from '@/components/SaveToCollectionModal';

// Heuristic: does the user's message look like a scan intent?
const SCAN_INTENT_RE = /\b(find|show|scan|search|look\s?for|any|deals?|near|within|under|posted|wanted|need|hiring|job|gig|lead|handyman|contractor)\b/i;

// Choose a "Deal" pill based on opportunity score.
const dealPill = (l) => {
  const score = l.opportunity?.opportunity_score;
  if (score == null) return null;
  if (score >= 85) return { text: 'Great Deal', klass: 'bg-zita-cocoa/15 text-zita-cocoa' };
  if (score >= 70) return { text: 'Good Deal', klass: 'bg-zita-gold/25 text-[hsl(24_60%_28%)]' };
  if (score >= 50) return { text: 'Fair Deal', klass: 'bg-zita-sand text-zita-mocha' };
  return null;
};

const opTypeLabel = {
  item: null,
  service_lead: 'Service Lead',
  job_posting: 'Job',
  gig: 'Gig',
  subcontractor: 'Subcontract',
  work_wanted: 'Work Wanted',
  wanted_ad: 'Wanted',
};

const Home = () => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'ari',
      text:
        "Hi — I'm Ari. Tell me what to find and I'll scan every source. I can look for items, deals, jobs, gigs, or service leads. What do you need?",
    },
  ]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  // Save-to-Collection modals
  const [savingListing, setSavingListing] = useState(null);
  const [savingAll, setSavingAll] = useState(null); // {listings, scanId, searchTerm}
  // ── Search Clarification Rule (Doctrine §12) ──
  const [pendingClarification, setPendingClarification] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const pollsRef = useRef({}); // scan_id -> interval id

  // Aug 2026 §Seek Interaction Model — Ari input must be permanently
  // usable. Focus it on mount so a customer can type IMMEDIATELY without
  // clicking anything first.
  useEffect(() => {
    const t = setTimeout(() => {
      try { inputRef.current?.focus(); } catch { /* ignore */ }
    }, 150);
    return () => clearTimeout(t);
  }, []);

  // Aug 2026 §9 — session persistence.
  // localStorage remembers ONLY the current session_id; the server is
  // authoritative for message history. If the stored session is fresh, we
  // reuse it; otherwise we ask the server for a new one and stash it.
  useEffect(() => {
    (async () => {
      let existing = null;
      try { existing = localStorage.getItem('zita-active-session'); } catch { /* ignore */ }
      if (existing) {
        setSessionId(existing);
        // Rehydrate messages from server for this session.
        try {
          const { data } = await api.get(`/ari/chat/history?session_id=${encodeURIComponent(existing)}`);
          if (Array.isArray(data.messages) && data.messages.length) {
            const rehydrated = data.messages.map((m, i) => ({
              id: `hist-${i}-${m.created_at || i}`,
              role: m.role === 'ari' ? 'ari' : 'user',
              text: m.text,
            }));
            setMessages([{
              id: 'welcome',
              role: 'ari',
              text: "Welcome back — picking up where we left off.",
            }, ...rehydrated]);
          }
        } catch { /* ignore — session may be new or empty */ }
      } else {
        try {
          const { data } = await api.post('/ari/chat/new-session');
          setSessionId(data.session_id);
          try { localStorage.setItem('zita-active-session', data.session_id); } catch { /* ignore */ }
        } catch { /* handled */ }
      }
    })();
    return () => {
      Object.values(pollsRef.current).forEach(clearInterval);
    };
  }, []);

  // Aug 2026 §7 — reconnect to any still-running scans launched from a
  // previous mount (background scans continue server-side; only the
  // frontend polling was tied to the previous mount).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/scans/active');
        (data?.active || []).forEach((s) => {
          const aiId = `${s.scan_id}-reconnect`;
          appendMessage({
            id: aiId,
            role: 'ari',
            text: `Still scanning: ${s.search_term || s.command_text || 'active search'}…`,
            scan: { scan_id: s.scan_id, status: s.status, filters: {}, groups: [], total: 0, source_progress: [] },
          });
          pollScan(aiId, s.scan_id);
        });
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newConversation = async () => {
    // Aug 2026 §Seek Interaction Model — New Conversation starts a fresh
    // thread. It DOES NOT delete/clear the previous conversation (that
    // still lives in Vault → Conversations). It DOES NOT clear the
    // current draft input (the user may want to type the same thing into
    // the fresh thread). Focus the input so typing works immediately.
    try {
      const { data } = await api.post('/ari/chat/new-session');
      setSessionId(data.session_id);
      try { localStorage.setItem('zita-active-session', data.session_id); } catch { /* ignore */ }
    } catch { /* ignore */ }
    setMessages([{
      id: 'welcome',
      role: 'ari',
      text: "Fresh thread. What can I look into for you?",
    }]);
    setPendingClarification(null);
    // Defensive — if busy somehow got stuck from a prior failure, clear it.
    setBusy(false);
    setTimeout(() => {
      try { inputRef.current?.focus(); } catch { /* ignore */ }
    }, 100);
  };

  const clearDraft = () => {
    // Aug 2026 §Seek Interaction Model — Reset Input clears the DRAFT
    // only. It does not touch conversation history and it does not create
    // a new session.
    setInput('');
    setTimeout(() => {
      try { inputRef.current?.focus(); } catch { /* ignore */ }
    }, 30);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const appendMessage = (msg) => setMessages((m) => [...m, { ...msg, id: msg.id || `${Date.now()}-${Math.random()}` }]);

  const updateMessage = (id, patch) =>
    setMessages((m) => m.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const pollScan = (aiMsgId, scan_id) => {
    const tick = async () => {
      try {
        const [{ data: s }, { data: g }] = await Promise.all([
          api.get(`/scans/${scan_id}`),
          api.get(`/scans/${scan_id}/grouped`),
        ]);
        const groups = g.groups || [];
        const total = groups.reduce((n, x) => n + (x.count || x.listings.length || 0), 0);
        updateMessage(aiMsgId, {
          scan: { scan_id, status: s.status, filters: s.filters, groups, total, source_progress: s.source_progress || [] },
        });
        if (['complete', 'completed', 'completed_with_source_issues',
             'partially_completed', 'partial', 'failed', 'awaiting_user_action',
             'cancelled'].includes(s.status)) {
          clearInterval(pollsRef.current[scan_id]);
          delete pollsRef.current[scan_id];
          // Finalize Ari text
          const summary = buildAriSummary(s, groups, total);
          updateMessage(aiMsgId, { text: summary });
        }
      } catch {
        clearInterval(pollsRef.current[scan_id]);
        delete pollsRef.current[scan_id];
      }
    };
    tick();
    pollsRef.current[scan_id] = setInterval(tick, 2500);
  };

  const runScan = async (text, opts = {}) => {
    try {
      const { data } = await api.post('/scans/start', {
        command_text: text,
        skip_clarification: !!opts.skipClarification,
        // Aug 2026 §9 — link the scan to this conversation so the same
        // thread can display it alongside conversational context.
        session_id: sessionId,
      });
      const kind = data.filters?.search_type === 'leads' ? 'leads' : 'items';
      const aiId = `${Date.now()}-ari`;
      appendMessage({
        id: aiId,
        role: 'ari',
        text: kind === 'leads'
          ? `Scanning every active source for ${data.filters.item_query || 'matching leads'}…`
          : `Scanning every active source for ${data.filters.item_query || 'matches'}…`,
        scan: { scan_id: data.scan_id, status: 'queued', filters: data.filters, groups: [], total: 0, source_progress: [] },
      });
      pollScan(aiId, data.scan_id);
      // Tell the extension we've launched a new Guided Scan — it will attach
      // its interruption beacons to this scan id.
      pushActiveScanToExtension(data.scan_id);
      // Clarification satisfied — reset the pending flag once a scan launches.
      setPendingClarification(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // ── Search Clarification Rule (Doctrine §12) ──
      // Backend returned 422 with a follow-up question instead of running a
      // wasteful empty search. Ari asks the question in the chat and stays
      // silent until the user replies with clarifying detail. The next user
      // message is routed straight to runScan via `pendingClarification`.
      if (e.response?.status === 422 && detail?.code === 'clarification_needed') {
        appendMessage({
          role: 'ari',
          text: detail.question || 'Could you give me a bit more detail so I can search properly?',
        });
        setPendingClarification({ originalText: text });
        return;
      }
      if (e.response?.status === 402) {
        appendMessage({ role: 'ari', text: 'Your Zita access is paused. Head to /subscribe to reactivate your account and continue.' });
      } else {
        appendMessage({ role: 'ari', text: `I couldn't start the scan. ${detail || e.message}` });
      }
    }
  };

  const runChat = async (text) => {
    if (!sessionId) return;
    const aiId = `${Date.now()}-ari`;
    appendMessage({ id: aiId, role: 'ari', text: '', streaming: true });
    const token = getAuthToken();
    try {
      const res = await fetch(`${API_BASE}/ari/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, text, attachments: [] }),
      });
      if (!res.ok || !res.body) {
        const err = await res.text();
        updateMessage(aiId, { text: `Error: ${err.slice(0, 300)}`, streaming: false });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split('\n')) {
          if (line.startsWith('data: ')) {
            const payload = line.slice(6);
            if (payload === '[DONE]') continue;
            acc += payload;
            updateMessage(aiId, { text: acc });
          }
        }
      }
      updateMessage(aiId, { streaming: false });
    } catch (e) {
      updateMessage(aiId, { text: `Error: ${e.message}`, streaming: false });
    }
  };

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || busy) return;
    setInput('');
    setBusy(true);
    appendMessage({ role: 'user', text: t });
    try {
      // If Ari just asked a clarifying question, treat the user's reply as
      // the clarification — combine it with the original vague message so
      // Ari has the full search context, and bypass the gate.
      if (pendingClarification) {
        const combined = `${pendingClarification.originalText} — ${t}`;
        await runScan(combined, { skipClarification: true });
      } else if (SCAN_INTENT_RE.test(t)) {
        await runScan(t);
      } else {
        await runChat(t);
      }
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        const fd = new FormData();
        fd.append('file', blob, 'voice.webm');
        try {
          const { data } = await api.post('/ari/voice/transcribe', fd);
          if (data.text) send(data.text);
        } catch { toast.error('Transcription failed'); }
      };
      rec.start();
      setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
    setRecording(false);
  };

  const playTts = async (text) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/ari/voice/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: text.slice(0, 3800) }),
      });
      const blob = await res.blob();
      new Audio(URL.createObjectURL(blob)).play();
    } catch { /* ignore */ }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-screen">
      <header className="px-6 md:px-10 pt-8 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Sparkles size={24} className="text-zita-cocoa" />
            <h1 className="font-serif text-4xl sm:text-5xl text-zita-espresso">Seek</h1>
          </div>
          <div className="flex items-center gap-2">
            <SeekAlertsBell />
            <SeekOverflowMenu />
            <button
              type="button"
              onClick={newConversation}
              data-testid="seek-new-conversation"
              className="text-sm text-zita-mocha hover:text-zita-espresso hover:underline"
            >
              + New conversation
            </button>
          </div>
        </div>
        <p className="text-zita-mocha mt-2">Tell Ari what you're after. She'll scan every source and keep the thread.</p>
      </header>

      <div ref={scrollRef} data-testid={ARI.messages} className="flex-1 overflow-y-auto zita-scroll px-4 md:px-10 pb-4">
        <div className="max-w-3xl mx-auto space-y-5">
          {messages.map((m) => (
            <MessageBubble
              key={m.id || m.role + m.text}
              m={m}
              onPlayTts={playTts}
              onSaveListing={setSavingListing}
              onSaveAll={(listings, scanId, searchTerm) => setSavingAll({ listings, scanId, searchTerm })}
            />
          ))}
          {busy && messages[messages.length - 1]?.role === 'user' && (
            <div className="flex justify-start">
              <div className="rounded-3xl bg-zita-sand text-zita-mocha px-5 py-3 text-sm">Ari is thinking…</div>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 md:px-10 pb-6">
        <div className="max-w-3xl mx-auto zita-card px-4 py-3 flex items-center gap-3">
          <button type="button" className="p-2 rounded-full text-zita-mocha hover:bg-zita-sand" aria-label="Attach">
            <Paperclip size={18} />
          </button>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Ask Ari anything…"
            data-testid={ARI.input}
            className="flex-1 bg-transparent outline-none text-base placeholder:text-zita-muted"
          />
          {input && (
            <button
              type="button"
              onClick={clearDraft}
              data-testid="seek-input-clear"
              aria-label="Clear draft"
              title="Clear draft (does not affect conversation)"
              className="p-1 rounded-full text-zita-mocha hover:bg-zita-sand"
            >
              <span aria-hidden="true" className="text-lg leading-none">×</span>
            </button>
          )}
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            data-testid={ARI.mic}
            className={`p-2 rounded-full ${recording ? 'bg-red-100 text-red-600' : 'text-zita-mocha hover:bg-zita-sand'}`}
            aria-label={recording ? 'Stop' : 'Voice'}
          >
            {recording ? <Square size={18} /> : <Mic size={18} />}
          </button>
          <button
            type="button"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            data-testid={ARI.send}
            className="zita-ari-send"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
        <div className="max-w-3xl mx-auto mt-3 flex flex-wrap gap-2">
          {[
            'Show me the best deals on used appliances within 30 miles of Akron.',
            'Find free dryers within 25 miles',
            'Find appliance-repair leads within 30 miles',
            'Find handyman jobs posted today',
          ].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => send(s)}
              data-testid={SEEK.suggestionChip}
              className="text-xs px-3 py-1.5 rounded-full ari-chip hover:opacity-90"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {savingListing && (
        <SaveToCollectionModal
          listing={savingListing}
          scanId={savingListing.scan_id}
          searchTerm={savingListing._search_term}
          onClose={() => setSavingListing(null)}
          onSaved={() => setSavingListing(null)}
        />
      )}
      {savingAll && (
        <SaveToCollectionModal
          listings={savingAll.listings}
          scanId={savingAll.scanId}
          searchTerm={savingAll.searchTerm}
          onClose={() => setSavingAll(null)}
          onSaved={() => setSavingAll(null)}
        />
      )}
    </div>
  );
};

const MessageBubble = ({ m, onPlayTts, onSaveListing, onSaveAll }) => {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-3xl rounded-br-lg px-5 py-3 text-[15px] leading-relaxed shadow-sm"
          style={{ background: 'hsl(28 55% 88%)', color: 'hsl(22 55% 22%)' }}>
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] space-y-3">
        {m.text && (
          <div className="rounded-3xl rounded-bl-lg px-5 py-3 text-[15px] leading-relaxed whitespace-pre-wrap bg-white/70 border border-zita-taupe/40 shadow-sm text-zita-espresso">
            {m.text}
            {!m.streaming && m.text && (
              <button
                type="button"
                onClick={() => onPlayTts(m.text)}
                data-testid={ARI.playTts}
                className="mt-2 flex items-center gap-1 text-[11px] text-zita-mocha hover:ari-accent"
              >
                <Volume2 size={12} /> Play
              </button>
            )}
          </div>
        )}
        {m.scan && (m.scan.status === 'running' || m.scan.status === 'queued' || m.scan.total > 0) && (
          <ScanCard scan={m.scan} onSaveListing={onSaveListing} onSaveAll={onSaveAll} />
        )}
      </div>
    </div>
  );
};

const buildAriSummary = (scan, groups, total) => {
  const kind = scan.filters?.search_type === 'leads' ? 'lead' : 'listing';
  if (total === 0) {
    if (scan.status === 'failed') return "I couldn't reach any source this time. Try again in a moment.";
    return `I scanned every active source and didn't find any ${kind}s matching that. Try a broader search.`;
  }
  const strong = groups.reduce(
    (n, g) => n + (g.listings || []).filter((l) => l.opportunity?.label === 'strong_pickup').length,
    0,
  );
  const parts = [`Here's what I found — ${total} ${kind}${total !== 1 ? 's' : ''} across ${groups.length} source${groups.length !== 1 ? 's' : ''}.`];
  if (strong) parts.push(`${strong} strong pickup${strong !== 1 ? 's' : ''}.`);
  return parts.join(' ');
};

const ScanCard = ({ scan, onSaveListing, onSaveAll }) => {
  const isLeads = scan.filters?.search_type === 'leads';
  const flat = scan.groups.flatMap((g) => g.listings.map((l) => ({ ...l, _source_name: g.source_name, source_name: l.source_name || g.source_name })));
  flat.sort((a, b) => (b.opportunity?.opportunity_score || 0) - (a.opportunity?.opportunity_score || 0));
  const inProgress = scan.status === 'running' || scan.status === 'queued';
  const showHeader = inProgress || flat.length > 0;
  if (!showHeader) return null;

  const searchTerm = scan.filters?.item_query || '';

  const openAll = () => {
    flat.slice(0, 2).forEach((l) => window.open(l.external_url, '_blank', 'noopener,noreferrer'));
    if (flat.length > 2) toast.info(`Opened top 2 — expand below to open more.`);
  };
  const saveAll = () => {
    if (!flat.length) return;
    onSaveAll && onSaveAll(flat, scan.scan_id, searchTerm);
  };
  const saveScan = async () => {
    try {
      const { data } = await api.post('/collections/saved-scans', { scan_id: scan.scan_id });
      toast.success(`Saved scan snapshot · ${data.total_listings} items frozen`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error('Ari Collections add-on ($9.99/mo) required for saved scans.');
      } else {
        toast.error(e.response?.data?.detail || 'Could not save scan.');
      }
    }
  };
  const findSimilar = () => {
    if (flat[0]) {
      const kw = flat[0].title.split(' ').slice(0, 3).join(' ');
      toast.info(`Try: "Find more like ${kw}"`);
    }
  };

  return (
    <div className="rounded-3xl bg-white/80 border border-zita-taupe/50 shadow-sm p-5 space-y-4">
      <div className="text-[15px] text-zita-espresso font-medium">
        {isLeads ? 'Here are the leads I found' : 'Here are the best deals I found'}
        {scan.filters?.location_text ? ` near ${scan.filters.location_text}` : ''}:
      </div>

      {scan.status === 'running' || scan.status === 'queued' ? (
        <div className="space-y-2">
          {scan.source_progress?.slice(0, 6).map((p) => (
            <div key={p.source_key} className="flex items-center justify-between text-xs text-zita-mocha border border-zita-taupe/50 rounded-lg px-3 py-1.5">
              <span className="text-zita-espresso">{p.display_name}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${sourceStatusClass(p.status)}`}>
                {(p.status === 'complete' || p.status === 'completed')
                  ? `${p.listings_found} found`
                  : sourceStatusLabel(p.status)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="space-y-3">
        {flat.slice(0, 6).map((l) => (
          <ChatListingRow
            key={l.id}
            listing={l}
            onSave={() => onSaveListing && onSaveListing({ ...l, _search_term: searchTerm })}
          />
        ))}
      </div>

      {flat.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            onClick={openAll}
            data-testid={RESULTS.openListing}
            className="zita-btn-secondary text-sm"
          >
            Contact sellers
          </button>
          <button
            type="button"
            onClick={saveAll}
            data-testid="home-save-all"
            className="zita-btn-primary text-sm inline-flex items-center gap-1"
          >
            <FolderPlus size={13} /> Save all matches
          </button>
          <button
            type="button"
            onClick={saveScan}
            data-testid="home-save-scan"
            className="zita-btn-secondary text-sm inline-flex items-center gap-1"
            title="Freeze this entire scan as a saved snapshot"
          >
            <Save size={13} /> Save scan
          </button>
          <button
            type="button"
            onClick={findSimilar}
            className="zita-btn-secondary text-sm"
          >
            Find similar
          </button>
          <Link to={`/results/${scan.scan_id}`} className="ml-auto text-sm text-zita-cocoa hover:underline self-center">
            See all {flat.length} →
          </Link>
        </div>
      )}
    </div>
  );
};

const ChatListingRow = ({ listing: l, onSave }) => {
  const pill = dealPill(l);
  const opLabel = opTypeLabel[l.opportunity_type || 'item'];
  return (
    <div data-testid={RESULTS.listingCard} className="flex items-center gap-4">
      {l.photos && l.photos[0] ? (
        <img
          src={l.photos[0]}
          alt=""
          className="w-16 h-16 rounded-xl object-cover border border-zita-taupe"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-zita-sand border border-zita-taupe" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <a href={l.external_url} target="_blank" rel="noopener noreferrer" className="font-medium text-zita-espresso hover:underline truncate">
            {l.title}
          </a>
          {opLabel && (
            <span className="text-[10px] uppercase tracking-wider ari-chip px-2 py-0.5 rounded-full">{opLabel}</span>
          )}
        </div>
        <div className="text-sm text-zita-mocha mt-0.5 flex flex-wrap gap-x-3">
          <span className="font-semibold text-zita-espresso">
            {l.opportunity_type && l.opportunity_type !== 'item'
              ? (l.budget ? `${fmtCurrency(l.budget)} budget` : 'No budget listed')
              : (l.is_free ? 'Free' : fmtCurrency(l.price))}
          </span>
          {l.location_text && <span>{l.location_text}</span>}
          {l.distance_miles != null && <span>· {fmtDistance(l.distance_miles)} away</span>}
          {l.posted_at && <span>· {timeAgo(l.posted_at)}</span>}
          <span>· {l._source_name}</span>
        </div>
      </div>
      {onSave && (
        <button
          type="button"
          onClick={onSave}
          aria-label="Save to collection"
          data-testid={`home-save-listing-${l.id}`}
          className="p-1.5 rounded-full hover:bg-zita-sand text-zita-cocoa"
        >
          <BookmarkPlus size={14} />
        </button>
      )}
      {pill && (
        <span data-testid={RESULTS.opportunityBadge} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full ${pill.klass}`}>
          {pill.text}
        </span>
      )}
    </div>
  );
};

export default Home;


// ============================================================================
// Aug 2026 §13 — Alerts contextual bell (opens inline dropdown; no navigation)
// ============================================================================

const SeekAlertsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dismissing, setDismissing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/alerts');
      // Backend returns only ACTIVE (undismissed) alerts by default.
      const rows = data.active || data.alerts || [];
      setAlerts(rows);
      setUnread(data.unread_count != null ? data.unread_count : rows.length);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post('/alerts/read-all');
      setAlerts([]);
      setUnread(0);
    } catch { /* silent */ }
  };

  const dismissOne = async (id, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setDismissing(id);
    try {
      await api.post(`/alerts/${id}/dismiss`);
      // Optimistic update — remove from active list immediately.
      setAlerts((prev) => prev.filter((a) => (a.id || a._id) !== id));
      setUnread((n) => Math.max(0, n - 1));
    } catch { /* silent */ } finally { setDismissing(null); }
  };

  const openAlert = async (alert) => {
    const id = alert.id || alert._id;
    try {
      const { data } = await api.post(`/alerts/${id}/acknowledge`);
      // Remove from active + decrement badge.
      setAlerts((prev) => prev.filter((a) => (a.id || a._id) !== id));
      setUnread((n) => Math.max(0, n - 1));
      // Deep-link to the object that produced the alert.
      const a = data?.alert || alert;
      if (a?.listing_ids?.[0]) {
        setOpen(false);
        navigate(`/results/${a.scan_id || alert.scan_id}#listing-${a.listing_ids[0]}`);
      } else if (a?.scan_id || alert.scan_id) {
        setOpen(false);
        navigate(`/results/${a.scan_id || alert.scan_id}`);
      } else if ((a?.kind || alert?.kind) === 'payment_failed'
                 || (a?.kind || alert?.kind) === 'trial_ending') {
        setOpen(false);
        navigate('/billing');
      }
    } catch { /* silent */ }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="seek-alerts-bell"
        aria-label="Alerts"
        className="relative p-2 rounded-full hover:bg-zita-sand text-zita-mocha"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="seek-alerts-unread-badge"
            className="absolute -top-0.5 -right-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-semibold min-w-[16px] text-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            data-testid="seek-alerts-dropdown"
            className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] zita-card p-0 z-40 shadow-xl"
          >
            <div className="px-4 py-3 border-b border-zita-taupe/60 flex items-center justify-between">
              <div className="font-serif text-lg text-zita-espresso">Alerts</div>
              {alerts.length > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  data-testid="seek-alerts-mark-all-read"
                  className="text-xs text-zita-mocha hover:text-zita-espresso hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto zita-scroll">
              {loading ? (
                <div className="p-4 text-sm text-zita-mocha">Loading…</div>
              ) : alerts.length === 0 ? (
                <div className="p-6 text-center text-sm text-zita-mocha" data-testid="seek-alerts-empty">
                  All caught up. Ari will let you know when a Loop Scan finds something new.
                </div>
              ) : (
                <ul className="divide-y divide-zita-taupe/50">
                  {alerts.slice(0, 20).map((a) => {
                    const id = a.id || a._id;
                    return (
                      <li
                        key={id}
                        data-testid={`seek-alert-item-${id}`}
                        className="group px-4 py-3 text-sm hover:bg-zita-sand cursor-pointer flex items-start gap-3"
                        onClick={() => openAlert(a)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-zita-espresso font-medium truncate">
                            {a.title || 'Update'}
                          </div>
                          {a.body && (
                            <div className="text-zita-mocha text-xs mt-1 line-clamp-2">
                              {a.body}
                            </div>
                          )}
                          {a.created_at && (
                            <div className="text-[11px] text-zita-muted mt-1">
                              {new Date(a.created_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => dismissOne(id, e)}
                          disabled={dismissing === id}
                          data-testid={`seek-alert-dismiss-${id}`}
                          aria-label="Dismiss alert"
                          className="p-1 rounded-full text-zita-mocha hover:bg-zita-taupe/50 opacity-70 hover:opacity-100 shrink-0"
                        >
                          {dismissing === id ? <Loader2 size={12} className="animate-spin" /> : <span aria-hidden="true">×</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-4 py-2 border-t border-zita-taupe/60 text-right">
              <Link
                to="/alerts"
                onClick={() => setOpen(false)}
                data-testid="seek-alerts-view-all"
                className="text-xs text-zita-mocha hover:text-zita-espresso hover:underline"
              >
                View history →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// Aug 2026 §12 — Rules & source preferences overflow menu (Seek context)
// ============================================================================

const SeekOverflowMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="seek-overflow-menu"
        aria-label="More"
        className="p-2 rounded-full hover:bg-zita-sand text-zita-mocha"
      >
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            data-testid="seek-overflow-dropdown"
            className="absolute right-0 mt-2 w-64 zita-card p-1 z-40 shadow-xl text-sm"
          >
            <Link
              to="/rules"
              onClick={() => setOpen(false)}
              data-testid="seek-overflow-rules"
              className="flex items-center gap-2 px-3 py-2 rounded hover:bg-zita-sand text-zita-espresso"
            >
              <Filter size={14} /> Search Rules
              <span className="ml-auto text-[10px] text-zita-muted">how Ari filters</span>
            </Link>
            <Link
              to="/connect"
              onClick={() => setOpen(false)}
              data-testid="seek-overflow-sources"
              className="flex items-center gap-2 px-3 py-2 rounded hover:bg-zita-sand text-zita-espresso"
            >
              <Sparkles size={14} /> Source preferences
              <span className="ml-auto text-[10px] text-zita-muted">which sources</span>
            </Link>
            <Link
              to="/seek/advanced"
              onClick={() => setOpen(false)}
              data-testid="seek-overflow-advanced"
              className="flex items-center gap-2 px-3 py-2 rounded hover:bg-zita-sand text-zita-espresso"
            >
              <Sparkles size={14} /> Advanced Seek (structured filters)
            </Link>
          </div>
        </>
      )}
    </div>
  );
};
